import { useCallback, useEffect, useRef, useState } from 'react';

export interface PaginatedResponse<T> {
  results?: T[];
  hasMore: boolean;
}

export interface UseLoadMoreOptions<T> {
  fetchFn: (params: { page: number }) => Promise<PaginatedResponse<T>>;
  skip?: boolean;
  resetDeps?: unknown[];
}

export interface UseLoadMoreResult<T> {
  rows: T[];
  isLoading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
  total?: number;
}

export const useLoadMore = <T>({
  skip = false,
  resetDeps = [],
  fetchFn,
}: UseLoadMoreOptions<T>): UseLoadMoreResult<T> => {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isLoadingRef = useRef(false);

  const fetchPage = useCallback(
    async (pageNumber: number, append = false) => {
      if (isLoadingRef.current || skip) return;

      isLoadingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const { results, hasMore } = await fetchFn({ page: pageNumber });

        if (append) {
          setRows((prev) => [...prev, ...(results ?? [])]);
        } else {
          setRows(results ?? []);
        }

        // Determine if there are more pages
        setHasMore(hasMore);
        setPage(pageNumber);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Fetch failed'));
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [fetchFn, skip],
  );

  // Reset and fetch first page when resetDeps change
  useEffect(() => {
    setRows([]);
    setHasMore(false);
    setTotal(undefined);
    setPage(1);
    setError(null);

    if (!skip) {
      fetchPage(1, false);
    }
  }, [...resetDeps, skip]);

  const loadMore = useCallback(() => {
    if (isLoadingRef.current || !hasMore) return;
    return fetchPage(page + 1, true);
  }, [fetchPage, page, hasMore]);

  const refetch = useCallback(() => {
    setRows([]);
    setHasMore(false);
    setPage(1);
    return fetchPage(1, false);
  }, [fetchPage]);

  return {
    error,
    hasMore,
    isLoading,
    rows,
    total,
    loadMore,
    refetch,
  };
};
